import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Zap, Phone, Mail, FileText, DollarSign, Clock, 
  TrendingUp, Users, Package, AlertTriangle, CheckCircle,
  Send, Download, RefreshCw, Calendar, Target, Flame
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SoloCommandCenter = () => {
  const [briefing, setBriefing] = useState(null);
  const [topLeads, setTopLeads] = useState([]);
  const [followUps, setFollowUps] = useState(null);
  const [overduePayments, setOverduePayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [briefingRes, leadsRes, followUpsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/solo/daily-briefing`),
        axios.get(`${API}/solo/lead-scoring/top-leads?limit=10`),
        axios.get(`${API}/solo/follow-ups/pending`),
        axios.get(`${API}/solo/payments/overdue`)
      ]);
      
      setBriefing(briefingRes.data);
      setTopLeads(leadsRes.data.top_leads || []);
      setFollowUps(followUpsRes.data);
      setOverduePayments(paymentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load command center data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const sendFollowUpBatch = async (type) => {
    try {
      const res = await axios.post(`${API}/solo/follow-ups/send-batch?follow_up_type=${type}`);
      toast.success(`Queued ${res.data.count} follow-up emails`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to send follow-ups');
    }
  };

  const sendPaymentReminders = async (type) => {
    try {
      const res = await axios.post(`${API}/solo/payments/send-reminders?reminder_type=${type}`);
      toast.success(`Queued ${res.data.count} payment reminders`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const sendDailyBriefing = async () => {
    try {
      await axios.post(`${API}/solo/daily-briefing/send-email?email=givi@tempusdispatch.com`);
      toast.success('Daily briefing sent to your email!');
    } catch (error) {
      toast.error('Failed to send briefing');
    }
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 70) return 'bg-red-500';
    if (score >= 50) return 'bg-orange-500';
    if (score >= 30) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Solo Command Center</h1>
            <p className="text-muted-foreground mt-1">{briefing?.greeting}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={sendDailyBriefing}
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Email Briefing
            </Button>
            <Button 
              onClick={fetchAllData} 
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Loads</p>
                  <p className="text-2xl font-bold">{briefing?.summary?.active_loads || 0}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Hot Leads</p>
                  <p className="text-2xl font-bold">{briefing?.summary?.hot_leads || 0}</p>
                </div>
                <Flame className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Follow-ups</p>
                  <p className="text-2xl font-bold">{briefing?.summary?.pending_follow_ups || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold">${(briefing?.summary?.overdue_amount || 0).toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Priority Actions */}
        {briefing?.priority_actions?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Today's Priority Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {briefing.priority_actions.map((action, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-accent/50 rounded-lg">
                    <span className="text-2xl">{action.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium">{action.action}</p>
                      <p className="text-sm text-muted-foreground">
                        {Array.isArray(action.details) ? action.details.join(', ') : action.details}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hot Leads */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Top 10 Leads to Contact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {topLeads.map((lead, idx) => (
                  <div key={lead.id} className="flex items-center gap-3 p-3 bg-accent/30 rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{lead.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {lead.lead_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{lead.company}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getScoreBadgeColor(lead.score)} text-white`}>
                        {lead.score}
                      </Badge>
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="p-2 hover:bg-green-500/20 rounded-full transition-colors">
                          <Phone className="w-4 h-4 text-green-500" />
                        </a>
                      )}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="p-2 hover:bg-blue-500/20 rounded-full transition-colors">
                          <Mail className="w-4 h-4 text-blue-500" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Follow-ups */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Pending Follow-ups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 3-Day Follow-ups */}
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">3-Day Check-in</p>
                      <p className="text-sm text-muted-foreground">
                        {followUps?.follow_ups?.['3_day']?.count || 0} contacts need a follow-up email
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => sendFollowUpBatch('3_day')}
                      disabled={!followUps?.follow_ups?.['3_day']?.count}
                      className="gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send All
                    </Button>
                  </div>
                </div>

                {/* 7-Day Follow-ups */}
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">7-Day Call List</p>
                      <p className="text-sm text-muted-foreground">
                        {followUps?.follow_ups?.['7_day']?.count || 0} contacts need a phone call
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => sendFollowUpBatch('7_day')}
                      disabled={!followUps?.follow_ups?.['7_day']?.count}
                      className="gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send Email
                    </Button>
                  </div>
                </div>

                {/* 14-Day Follow-ups */}
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">14-Day Final Notice</p>
                      <p className="text-sm text-muted-foreground">
                        {followUps?.follow_ups?.['14_day']?.count || 0} contacts - last chance
                      </p>
                    </div>
                    <Badge variant="destructive">Action Required</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Reminders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Payment Collection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 7-Day Overdue */}
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">7 Days Overdue</p>
                      <p className="text-sm text-muted-foreground">
                        {overduePayments?.overdue?.['7_day']?.count || 0} invoices • 
                        ${(overduePayments?.overdue?.['7_day']?.total_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => sendPaymentReminders('7_day')}
                      disabled={!overduePayments?.overdue?.['7_day']?.count}
                    >
                      Send Reminder
                    </Button>
                  </div>
                </div>

                {/* 14-Day Overdue */}
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">14 Days Overdue</p>
                      <p className="text-sm text-muted-foreground">
                        {overduePayments?.overdue?.['14_day']?.count || 0} invoices • 
                        ${(overduePayments?.overdue?.['14_day']?.total_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => sendPaymentReminders('14_day')}
                      disabled={!overduePayments?.overdue?.['14_day']?.count}
                    >
                      Urgent Reminder
                    </Button>
                  </div>
                </div>

                {/* 30-Day Overdue */}
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">30+ Days Overdue</p>
                      <p className="text-sm text-muted-foreground">
                        {overduePayments?.overdue?.['30_day']?.count || 0} invoices • 
                        ${(overduePayments?.overdue?.['30_day']?.total_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => sendPaymentReminders('30_day')}
                      disabled={!overduePayments?.overdue?.['30_day']?.count}
                    >
                      Final Notice
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => window.location.href = '/post-load'}>
                  <Package className="w-6 h-6" />
                  <span>Post Load</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => window.location.href = '/ai-dispatcher'}>
                  <Target className="w-6 h-6" />
                  <span>AI Dispatcher</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => window.location.href = '/admin/brokers'}>
                  <Users className="w-6 h-6" />
                  <span>Brokers CRM</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2" onClick={() => window.location.href = '/admin/invoices'}>
                  <FileText className="w-6 h-6" />
                  <span>Invoices</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SoloCommandCenter;
